/**
 * Labels — the one-line description every knowledge entry and every project
 * carries. Agents choose what to read from labels alone (search results, the
 * project map, entry lists) before loading any content, so a label is short,
 * says what the thing is and when it's useful, and leads with the words a
 * search would use.
 *
 * One rule for every authored write, on every surface: one line, at most
 * {@link LABEL_MAX} characters. The request contracts and the tool schemas
 * state it up front (the limit is in each tool's `maxLength` and in the
 * parameter text, so a model that ignores schema keywords still reads it);
 * the stores refuse anything that breaks it, so enforcement never depends on
 * the model. Restores and sync put records back as they were and skip it.
 */

/** Longest label a knowledge entry or project may carry. */
export const LABEL_MAX = 200;

/** What a good knowledge label says — shown to agents and in the dashboard. */
export const KNOWLEDGE_LABEL_GUIDE = `One line, at most ${LABEL_MAX} characters: what this entry is and when it's useful, key words first. Blank writes one from the content's first line.`;

/** What a good project description says — shown to agents and in the dashboard. */
export const PROJECT_LABEL_GUIDE = `One line, at most ${LABEL_MAX} characters: what the project is and what work belongs in it. Every project has one — it can be replaced, not cleared.`;

/** The `description` parameter of the knowledge tools (desktop and cloud). */
export const KNOWLEDGE_LABEL_PARAM = {
  type: 'string',
  maxLength: LABEL_MAX,
  description: KNOWLEDGE_LABEL_GUIDE,
} as const;

/** The `description` parameter of the project tools (desktop and cloud). */
export const PROJECT_LABEL_PARAM = { type: 'string', maxLength: LABEL_MAX, description: PROJECT_LABEL_GUIDE } as const;

/** Why `text` can't be a label, or null when it can. An empty label is the caller's rule to decide. */
export function labelProblem(text: string): string | null {
  if (/[\r\n]/.test(text)) return 'must be one line';
  if (text.length > LABEL_MAX) return `is ${text.length} characters; the limit is ${LABEL_MAX}`;
  return null;
}

/**
 * Fit system-written text to the label rule: whitespace collapsed to one line,
 * cut at {@link LABEL_MAX} with an ellipsis. For labels OneStone composes
 * itself (routine output); a person's or an agent's label is refused instead.
 */
export function fitLabel(text: string): string {
  const line = text.replace(/\s+/g, ' ').trim();
  return line.length <= LABEL_MAX ? line : `${line.slice(0, LABEL_MAX - 1).trimEnd()}…`;
}

/** Why `value` can't be a required label (a project's description), or null when it can. */
export function requiredLabelProblem(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim().length === 0) return 'is required';
  return labelProblem(value.trim());
}
